<!-- The frame of a Vue dialog: dark blurred overlay, the panel with the opening animation (.modal-animate), a header
     with the title and ×, a scrollable body (default slot) and a footer for the buttons (slot "footer"). Same markup and
     classes as the legacy edit dialogs of index.html (#videoModal; before: #sourceEditModal, #creatorEditModal…), teleported to <body>.
     It registers itself in the dialog stack (src/stores/dialogs.ts) while it is on screen: Esc (only with `esc`) is
     handled by the global shortcuts (src/features/keyboard.ts) through the dialogs store's keydown. It never closes itself: it emits "close" (×, ‹,
     outside click with `outside`, Esc with `esc`) and the owner hides it; with `save`, Ctrl/Cmd+S and Ctrl/Cmd+Enter save it. Recipe: docs/migration.md, "Dialogs and fields". -->
<script setup lang="ts">
import { useDialogEntry } from '../composables/useDialogEntry'
import { dialogWidth } from '../utils/dialogWidth'
import { useI18n } from '../composables/useI18n'

const props = withDefaults(defineProps<{
  /** Dialog name in the stack (isDialogOpen) */
  name: string
  /** id of the overlay (keep the legacy one: tests and other code look for it) */
  id?: string
  title?: string
  /** id of the <h3> title */
  titleId?: string
  /** Extra classes of the <h3> title, written whole (e.g. 'truncate' for a long title: the Drive files dialog) */
  titleClass?: string
  /** Panel width class, written whole (wider on large screens: dialogWidth): 'max-w-xl' (default), 'max-w-2xl', 'max-w-lg', 'max-w-md' */
  width?: string
  /** z-index class, written whole: 'z-50' (default, the edit dialogs), 'z-[55]'… (docs/architecture.md, z-index layers) */
  z?: string
  /** Edit-dialog header: ‹ on the left, title in the middle, × on the right, and the overlay scrolls. false: title on
   *  the left and × (the Manage tags / Profiles look) */
  back?: boolean
  /** A click on the overlay closes it (the legacy dialogs with onclick="if(event.target===this)close()") */
  outside?: boolean
  /** Esc closes it, and other global shortcuts are blocked while it is the topmost (a step of src/features/keyboard.ts) */
  esc?: boolean
  /** The user types data in it: the online sync waits until it closes (src/features/cloud/autosync.ts) */
  editing?: boolean
  /** Saves the dialog (an edit dialog): Ctrl/Cmd+S and Ctrl/Cmd+Enter call it while it is the topmost (dialog stack) */
  save?: () => void
  bodyClass?: string
  footerClass?: string
}>(), {
  save: undefined,
  id: undefined,
  title: '',
  titleId: undefined,
  titleClass: '',
  width: 'max-w-xl',
  z: 'z-50',
  back: true,
  outside: false,
  esc: false,
  editing: false,
  bodyClass: 'p-6 overflow-y-auto space-y-5 flex-1 text-sm',
  footerClass: 'p-4 border-t border-gray-800 bg-darkcard flex gap-3 flex-shrink-0'
})
const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
const { onDown, onClick } = useDialogEntry({ name: props.name, esc: props.esc, editing: props.editing, save: props.save ? () => props.save?.() : undefined },
  () => emit('close'), () => props.outside)
</script>

<template>
  <Teleport to="body">
    <div :id="id" :class="['fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 md:p-6', z, { 'overflow-y-auto': back }]"
      @mousedown="onDown" @click="onClick">
      <div :class="['bg-darkcard border border-gray-800 w-full rounded-2xl overflow-hidden modal-animate max-h-[92dvh] flex flex-col shadow-2xl', dialogWidth(width)]">
        <div :class="['flex justify-between items-center px-6 py-4 border-b border-gray-800 flex-shrink-0', { 'bg-darkcard': back }]">
          <button v-if="back" type="button" :title="t('Back')" :aria-label="t('Back')" class="text-gray-400 hover:text-gray-100 text-lg cursor-pointer" @click="emit('close')"><i class="fas fa-chevron-left"></i></button>
          <h3 :id="titleId" :class="['font-bold text-base text-gray-200', titleClass]"><slot name="title">{{ title }}</slot></h3>
          <button type="button" :title="t('Close')" :aria-label="t('Close')" :class="['text-gray-400 hover:text-gray-100 text-lg', { 'cursor-pointer': back }]" @click="emit('close')"><i class="fas fa-xmark"></i></button>
        </div>
        <div :class="bodyClass"><slot /></div>
        <div v-if="$slots.footer" :class="footerClass"><slot name="footer" /></div>
      </div>
    </div>
  </Teleport>
</template>
